"use client";
import { useMemo, useState } from "react";
import debounce from "lodash.debounce";
import sortBy from "lodash.sortby";

import {
  Avatar,
  Key,
  Text,
  Tooltip,
  TooltipFocusableTrigger,
  Typeahead,
  TypeaheadItem,
} from "@zapier/design-system";

import {
  Member,
  Team,
  useAccount,
  useQuerySearchMembersAndTeams,
} from "@zapier/client";
import styles from "./SharingTypeahead.module.css";

interface Props {
  isBusy?: boolean;
  isDisabled?: boolean;
  disableEveryoneOption?: boolean;
  showEveryoneOption?: boolean;
  onSelect: (selected: Key | null) => void;
  size?: "compact" | "medium" | "large";
}

interface MemberItem {
  id: Key;
  name: string;
  description: string;
  imageSrc: string | null;
  isDisabled?: boolean;
  disabledReason?: string;
}

function SharingTypeaheadItem({
  member,
  totalItems,
}: {
  member: MemberItem;
  totalItems: number;
}) {
  const content = (
    <div
      className={`${styles.typeaheadItem} 
        ${member.id === 0 && totalItems > 1 ? styles.typeaheadItemEveryone : ""}
        ${member.isDisabled ? styles.typeaheadItemDisabled : ""}`}
    >
      <Avatar name={member.name} url={member.imageSrc} />
      <Text tag="p" type="SmallPrint1">
        <strong>{member.name}</strong> - {member.description}
      </Text>
    </div>
  );

  return (
    <TypeaheadItem
      id={member.id}
      key={member.id}
      textValue={member.name}
      {...(member.isDisabled ? ({ isDisabled: true } as any) : {})}
    >
      {member.isDisabled && member.disabledReason ? (
        <Tooltip content={member.disabledReason} allowMultiline placement="top">
          <TooltipFocusableTrigger>{content}</TooltipFocusableTrigger>
        </Tooltip>
      ) : (
        content
      )}
    </TypeaheadItem>
  );
}

function SharingTypeahead({
  isBusy = false,
  isDisabled = false,
  disableEveryoneOption = false,
  showEveryoneOption = false,
  onSelect,
  size = "large",
}: Props) {
  const [inputValue, setInputValue] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const { account } = useAccount(); // needed to display "Everyone" option
  const { membersAndTeams } = useQuerySearchMembersAndTeams({
    search: searchValue,
  });

  const members = useMemo(() => {
    const _members = sortBy(
      membersAndTeams.map((member): MemberItem => {
        return {
          id: `${(member as Team)?.name ? "team_" : "customuser_"}${member.id}`,
          name: (member as Member)?.fullName || (member as Team)?.name || "",
          description:
            (member as Member)?.email || String((member as Team)?.memberCount),
          imageSrc: (member as Member)?.photoUrl || (member as Team)?.logo,
        };
      }),
      ({ name }: { name: string }) => {
        return name;
      },
    );

    // Check if the input value matches "everyone"
    const inputMatchesEveryone =
      !inputValue || "everyone".includes(inputValue.toLowerCase());

    // "Everyone" should always show at the top of the list unless it is already selected
    if (showEveryoneOption && inputMatchesEveryone) {
      return [
        {
          id: `account_${account?.id}`,
          name: "Everyone",
          description: `${account?.memberCount} member${account?.memberCount === 1 ? "" : "s"}`,
          imageSrc: account?.logoUrl || "",
          isDisabled: disableEveryoneOption,
          disabledReason:
            "Sharing app connections across your entire account has been disabled by your admin.",
        },
        ..._members,
      ];
    }

    return _members;
  }, [
    membersAndTeams,
    showEveryoneOption,
    inputValue,
    account,
    disableEveryoneOption,
  ]);

  const searchChange = debounce((value: string) => {
    setSearchValue(value);
  }, 500);

  const inputChange = (value: string) => {
    if (value !== inputValue) {
      setInputValue(value);
      searchChange(value);
    }
  };

  return (
    <div className={styles.root}>
      <Typeahead
        isBusy={isBusy}
        isDisabled={isDisabled}
        items={members as MemberItem[]}
        label="Share with:"
        onInputChange={inputChange}
        onChange={onSelect}
        placeholder="Search by name, email, or team"
        menuTrigger="focus"
        size={size}
      >
        {(member: any) => (
          <SharingTypeaheadItem
            member={member as MemberItem}
            totalItems={members.length}
          />
        )}
      </Typeahead>
    </div>
  );
}

export default SharingTypeahead;
